import React, { useState, useRef, useMemo } from "react";
import { registerPublicUser, checkNumistrailIdAvailable } from "../api/users";
import { validateNumistrailId, validateEmail, validatePassword, normalizeInput } from "../utils/validation";
import { useTranslation, Trans } from "react-i18next";

const LEGAL_VERSION_TOS = "tos_v5_2026_04";
const LEGAL_VERSION_PRIVACY = "privacy_v4_2026_03";
const LEGAL_VERSION_DISCLAIMER = "disclaimer_v3_2026_03";

export function Register() {
  const [form, setForm] = useState({ numistrail_id: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalTouched, setLegalTouched] = useState(false);
  const idInputRef = useRef(null);
  const { t } = useTranslation();

  const handleChange = (key) => (event) => {
    const value = key === "email" ? event.target.value.toLowerCase() : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "numistrail_id") {
      setSuggestion(null);
    }
  };

  const markTouched = (key) => () =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const validationErrors = useMemo(() => ({
    numistrail_id: validateNumistrailId(form.numistrail_id),
    email: validateEmail(form.email),
    password: validatePassword(form.password),
  }), [form.numistrail_id, form.email, form.password]);

  const isFormValid = useMemo(
    () => Object.values(validationErrors).every((message) => !message),
    [validationErrors]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (!isFormValid) {
        setTouched({ numistrail_id: true, email: true, password: true });
        setError(t("validation.fixErrors"));
        return;
      }
      if (!legalAccepted) {
        setLegalTouched(true);
        setError(t("validation.legalAcceptance.required"));
        return;
      }

      if (showPassword) {
        setShowPassword(false);
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      await registerPublicUser({
        numistrail_id: normalizeInput(form.numistrail_id, { lowercase: true }),
        password: normalizeInput(form.password),
        email: normalizeInput(form.email, { lowercase: true }),
        legal_acceptance: true,
        legal_version_tos: LEGAL_VERSION_TOS,
        legal_version_privacy: LEGAL_VERSION_PRIVACY,
        legal_version_disclaimer: LEGAL_VERSION_DISCLAIMER,
      });

      setError(null);
      setSuccess(true);
    } catch (err) {
      setSuggestion(null);
      setSuccess(false);
      const messageKey = err.response?.data?.message_key;
      const message = err.response?.data?.message;
      const resolvedMessage = t(messageKey || "errors.registerFailed", {
        defaultValue: message || t("errors.registerFailed"),
      });
      if (resolvedMessage) {
        setError(resolvedMessage);
        if ((message || "").includes("numistrail_id")) {
          const suggested = await suggestUsername(form.numistrail_id);
          if (suggested) setSuggestion(suggested);
        }
      } else {
        setError(t("errors.registerFailed"));
      }
    }
  };

  const suggestUsername = async (base) => {
    let attempt = 0;
    while (attempt < 5) {
      const normalizedBase = normalizeInput(base || "user", { lowercase: true }).replace(/[^a-z0-9_]/g, "").slice(0, 40) || "user";
      const candidate = `${normalizedBase}${Math.floor(Math.random() * 1000)}`;
      try {
        const res = await checkNumistrailIdAvailable(candidate);
        if (res.data.available) return candidate;
      } catch (e) {
        // ignore and try another
      }
      attempt += 1;
    }
    return null;
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((current) => ({ ...current, numistrail_id: suggestion }));
    setSuggestion(null);
    setError(null);
    setTimeout(() => idInputRef.current?.focus(), 0);
  };
  const isDisabled = success;
  const handleLegalChange = (event) => {
    const checked = event.target.checked;
    setLegalAccepted(checked);
    setLegalTouched(true);
    if (checked && error === t("validation.legalAcceptance.required")) {
      setError(null);
    }
  };

  return (
    <div className="register-screen">
      <div className="register-card">
        <div className="register-card__header">
          <h2 className="register-title">{t("register.title")}</h2>
          <p className="register-subtitle">
            {t("register.subtitle")}
          </p>
        </div>

        {error && (
          <div className="register-status register-status--error">{error}</div>
        )}

        {success && (
          <div className="register-status register-status--success">
            <strong>{t("register.successTitle")}</strong>
            <p>{t("register.successBody")}</p>
          </div>
        )}

        {suggestion && (
          <div className="register-status register-status--info">
            <span>{t("register.suggestedId")}</span>
            <button type="button" className="register-suggestion" onClick={applySuggestion}>
              {suggestion}
            </button>
          </div>
        )}

        <form className="register-form" onSubmit={handleSubmit} method="post" autoComplete="on">
          <div className="register-field">
            <label className="register-label" htmlFor="register-id">
              {t("register.idLabel")}
            </label>
            <input
              className="register-input"
              id="register-id"
              name="username"
              type="text"
              autoComplete="username"
              placeholder={t("register.idPlaceholder")}
              ref={idInputRef}
              value={form.numistrail_id}
              onChange={handleChange("numistrail_id")}
              onBlur={markTouched("numistrail_id")}
              disabled={isDisabled}
            />
            {touched.numistrail_id && validationErrors.numistrail_id ? (
              <p className="register-field__error" role="alert">
                {t(validationErrors.numistrail_id)}
              </p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="register-email">
              {t("register.emailLabel")}
            </label>
            <input
              className="register-input"
              id="register-email"
              name="email"
              placeholder={t("register.emailPlaceholder")}
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange("email")}
              onBlur={markTouched("email")}
              disabled={isDisabled}
            />
            {touched.email && validationErrors.email ? (
              <p className="register-field__error" role="alert">{t(validationErrors.email)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="register-password">
              {t("register.passwordLabel")}
            </label>
            <div className="login-password-input">
              <input
                className="register-input"
                id="register-password"
                name="password"
                placeholder={t("register.passwordPlaceholder")}
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange("password")}
                onBlur={markTouched("password")}
                autoComplete="new-password"
                disabled={isDisabled}
              />
              <button
                type="button"
                className="login-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                disabled={isDisabled}
              >
                {showPassword ? "\u{1F648}" : "\u{1F441}\uFE0F"}
              </button>
            </div>
            {touched.password && validationErrors.password ? (
              <p className="register-field__error" role="alert">{t(validationErrors.password)}</p>
            ) : null}
          </div>

          <div className="register-legal">
            <input
              id="register-legal"
              type="checkbox"
              checked={legalAccepted}
              onChange={handleLegalChange}
              onBlur={() => setLegalTouched(true)}
              disabled={isDisabled}
            />
            <label className="register-legal__label" htmlFor="register-legal">
              <Trans
                i18nKey="register.legalAcceptance"
                components={{
                  terms: (
                    <a href="/terms" target="_blank" rel="noopener noreferrer" />
                  ),
                  privacy: (
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" />
                  ),
                  disclaimer: (
                    <a href="/disclaimer" target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              />
            </label>
          </div>
          {legalTouched && !legalAccepted ? (
            <p className="register-field__error" role="alert">
              {t("validation.legalAcceptance.required")}
            </p>
          ) : null}

          <button
            className="register-submit"
            type="submit"
            disabled={!isFormValid || !legalAccepted || isDisabled}
          >
            {t("register.submit")}
          </button>
        </form>

        <p className="register-footer">
          {t("register.alreadyAccount")} <a href="/login">{t("register.loginLink")}</a>
        </p>
      </div>
    </div>
  );
}

